import { TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AssessmentAIScribeResult,
  FindingPair,
  FindingRow
} from '../../assessment-ai-scribe.model';

// Maps 0-10 numeric scale back to a human-readable label for display
function formatScale10(value: number | null, labels: { low: string; high: string }): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (value === 0) {
    return 'N/A';
  }
  if (value <= 2) {
    return labels.low + ' (' + value + '/10)';
  }
  if (value <= 4) {
    return 'Low-moderate (' + value + '/10)';
  }
  if (value <= 6) {
    return 'Moderate (' + value + '/10)';
  }
  if (value <= 8) {
    return labels.high + ' (' + value + '/10)';
  }
  return 'Very ' + labels.high.toLowerCase() + ' (' + value + '/10)';
}

@Component({
  selector: 'csi-scribe-results-panel',
  templateUrl: './scribe-results-panel.component.html',
  styleUrls: ['./scribe-results-panel.component.scss']
})
export class ScribeResultsPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() result: AssessmentAIScribeResult | null = null;
  @Input() rows: FindingRow[] = [];
  @Input() pairedRows: FindingPair[] = [];
  @Input() otherRows: FindingRow[] = [];
  @Input() activeRowCount = 0;
  @Input() errorMessage = '';
  @Input() browserTranscript = '';
  @Input() enabledSections: string[] = [];
  @Input() assessmentMethod: string | null = null;

  // Sections hidden for Simple/Screening mode (no clinical findings tab)
  private static readonly SIMPLE_HIDDEN_SECTIONS = ['findings'];
  // Sections hidden for PreOp/PreOpScreening (no assessment section)
  private static readonly PREOP_HIDDEN_SECTIONS: string[] = [];
  // Sections hidden for Treatment/FollowUp (no patientFile)
  private static readonly TREATMENT_HIDDEN_SECTIONS = ['patientFile'];

  /** Effective sections after filtering by assessment method */
  get effectiveEnabledSections(): string[] {
    const base =
      this.enabledSections && this.enabledSections.length > 0
        ? this.enabledSections
        : ['findings', 'patientProfile', 'history', 'symptoms', 'patientFile', 'assessment'];

    const method = this.assessmentMethod;
    const methodLower = method ? method.toLowerCase() : '';

    // Add dewsScreening section for DEWS III assessments if not already present
    // SymptomModes.DewsIII = 'dewsIII' (camelCase) — compare lowercase to be safe
    const withDews =
      methodLower === 'dewsiii' && base.indexOf('dewsScreening') === -1
        ? base.concat(['dewsScreening'])
        : base;

    if (!method) {
      return withDews;
    }

    let hidden: string[] = [];
    if (method === 'simple') {
      hidden = ScribeResultsPanelComponent.SIMPLE_HIDDEN_SECTIONS;
    } else if (method === 'treatment' || method === 'followUp') {
      hidden = ScribeResultsPanelComponent.TREATMENT_HIDDEN_SECTIONS;
    } else if (method === 'preop' || method === 'preOpScreening') {
      hidden = ScribeResultsPanelComponent.PREOP_HIDDEN_SECTIONS;
    }

    return withDews.filter(s => hidden.indexOf(s) === -1);
  }

  get hasDewsScreening(): boolean {
    const d = this.result && this.result.dewsScreening;
    if (!d) { return false; }
    return (
      d.suddenOnset !== null ||
      d.visionAffected !== null ||
      d.improvedByBlinking !== null ||
      d.frankPain !== null ||
      d.unilateral !== null ||
      d.facialFlushing !== null ||
      d.mouthDryness !== null ||
      d.enlargedSalivaryGlands !== null ||
      d.itching !== null ||
      d.eyesSwollen !== null ||
      d.crustyLashes !== null ||
      d.mucopurulentDischarge !== null
    );
  }

  get dewsScreeningCount(): number {
    const d = this.result && this.result.dewsScreening;
    if (!d) { return 0; }
    return [
      d.suddenOnset,
      d.visionAffected,
      d.improvedByBlinking,
      d.frankPain,
      d.unilateral,
      d.facialFlushing,
      d.mouthDryness,
      d.enlargedSalivaryGlands,
      d.itching,
      d.eyesSwollen,
      d.crustyLashes,
      d.mucopurulentDischarge
    ].filter(v => v !== null && v !== undefined).length;
  }

  transcriptExpanded = false;
  profileExpanded = true;
  signsExpanded = true;
  dewsScreeningExpanded = true;
  historyExpanded = true;
  historyGeneralExpanded = true;
  historyConditionsExpanded = true;
  historyEnvSymptomsExpanded = true;
  historyOtherConditionsExpanded = true;
  medicationsExpanded = true;
  symptomsExpanded = true;
  weatherExpanded = true;
  patientFileExpanded = true;
  impressionExpanded = true;

  // ── Section counts (fields actually extracted) ──────────────────────────

  get patientProfileCount(): number {
    const p = this.result && this.result.patientProfile;
    if (!p) {
      return 0;
    }
    return (
      (p.firstName ? 1 : 0) +
      (p.lastName ? 1 : 0) +
      (p.age !== null && p.age !== undefined ? 1 : 0) +
      (p.gender ? 1 : 0) +
      (p.email ? 1 : 0) +
      (p.dateOfBirth ? 1 : 0) +
      (p.address ? 1 : 0) +
      (p.city ? 1 : 0) +
      (p.province ? 1 : 0) +
      (p.postalCode ? 1 : 0) +
      (p.country ? 1 : 0) +
      (p.healthCardNumber ? 1 : 0) +
      (p.phoneNumber ? 1 : 0)
    );
  }

  get historyCount(): number {
    const h = this.result && this.result.history;
    if (!h) {
      return 0;
    }
    let n = 0;
    if (h.chiefComplaint) {
      n++;
    }
    if (h.conditions && h.conditions.length) {
      n += h.conditions.length;
    }
    if (h.environmentalSymptoms && h.environmentalSymptoms.length) {
      n += h.environmentalSymptoms.length;
    }
    if (h.drugAllergies) {
      n++;
    }
    if (h.seasonalAllergies) {
      n++;
    }
    if (h.otherAllergies) {
      n++;
    }
    if (h.occupation) {
      n++;
    }
    if (h.hobbies) {
      n++;
    }
    if (h.triggeringEvent) {
      n++;
    }
    if (h.previousTreatments) {
      n++;
    }
    if (h.sleepPerNight !== null && h.sleepPerNight !== undefined) {
      n++;
    }
    if (h.caffeinatedDrinksPerDay !== null && h.caffeinatedDrinksPerDay !== undefined) {
      n++;
    }
    if (h.makeupNotes) {
      n++;
    }
    if (h.diet) {
      n++;
    }
    if (h.dietQuality !== null && h.dietQuality !== undefined) {
      n++;
    }
    if (h.healthQuality !== null && h.healthQuality !== undefined) {
      n++;
    }
    if (h.stressLevel !== null && h.stressLevel !== undefined) {
      n++;
    }
    if (h.workHoursPerDay !== null && h.workHoursPerDay !== undefined) {
      n++;
    }
    if (h.exerciseHoursPerWeek !== null && h.exerciseHoursPerWeek !== undefined) {
      n++;
    }
    if (h.lastEyeExam) {
      n++;
    }
    if (h.changesToEyes) {
      n++;
    }
    if (h.symptomDuration) {
      n++;
    }
    if (h.waterPerDay !== null && h.waterPerDay !== undefined) {
      n++;
    }
    if (h.timeSpentOutdoors !== null && h.timeSpentOutdoors !== undefined) {
      n++;
    }
    if (h.timeSpentClosedEnvironments !== null && h.timeSpentClosedEnvironments !== undefined) {
      n++;
    }
    if (h.cityHumidityLevel !== null && h.cityHumidityLevel !== undefined) {
      n++;
    }
    if (h.cityPollutionLevel !== null && h.cityPollutionLevel !== undefined) {
      n++;
    }
    if (h.sensitivityToLight !== null && h.sensitivityToLight !== undefined) {
      n++;
    }
    if (h.sensitivityToWind !== null && h.sensitivityToWind !== undefined) {
      n++;
    }
    if (h.impactOnMood !== null && h.impactOnMood !== undefined) {
      n++;
    }
    if (h.impactOnDailyLife !== null && h.impactOnDailyLife !== undefined) {
      n++;
    }
    if (h.pastOcularHistory) {
      n++;
    }
    if (h.pastOcularMedications) {
      n++;
    }
    if (h.contactLensDurationType) {
      n++;
    }
    if (h.issuesFromPreviousTreatments) {
      n++;
    }
    if (h.urbanAreaTimePercent !== null && h.urbanAreaTimePercent !== undefined) {
      n++;
    }
    return n;
  }

  get medicationsCount(): number {
    const h = this.result && this.result.history;
    if (!h) return 0;
    return (
      (h.medications ? 1 : 0) + (h.currentOcularMedications ? 1 : 0) + (h.nutraceuticals ? 1 : 0)
    );
  }

  get symptomsCount(): number {
    const s = this.result && this.result.symptoms;
    if (!s) {
      return 0;
    }
    return (
      (s.osdiScore !== null && s.osdiScore !== undefined ? 1 : 0) +
      (s.speedScore !== null && s.speedScore !== undefined ? 1 : 0) +
      (s.speedIIScore !== null && s.speedIIScore !== undefined ? 1 : 0) +
      (s.osdi6Score !== null && s.osdi6Score !== undefined ? 1 : 0) +
      (s.deqScore !== null && s.deqScore !== undefined ? 1 : 0) +
      (s.fitzpatrickScore !== null && s.fitzpatrickScore !== undefined ? 1 : 0)
    );
  }

  get patientFileCount(): number {
    const pf = this.result && this.result.patientFile;
    if (!pf) {
      return 0;
    }
    let count = [
      pf.wearsGlasses,
      pf.eyeColor,
      pf.skinColor,
      pf.naturalHairColor,
      pf.freckles,
      pf.tanIntensity,
      pf.faceSunSensitivity,
      pf.longSunExposure,
      pf.darkerAfterSunExposure,
      pf.sunTanExposureFrequency,
      pf.lastSunExposure,
      pf.ethnicity,
      pf.education,
      pf.familyHistory,
      pf.eyelashModification,
      pf.sickBuildingSyndrome,
      pf.styeOrChalazion,
      pf.childBirth,
      pf.hematopoieticStemCellTransplantation
    ].filter(v => !!v).length;
    if (pf.gender && pf.gender.length) {
      count += pf.gender.length;
    }
    if (pf.pastOcularHistory && pf.pastOcularHistory.length) {
      count += pf.pastOcularHistory.length;
    }
    if (pf.pastSurgeries && pf.pastSurgeries.length) {
      count += pf.pastSurgeries.length;
    }
    return count;
  }

  get hasPatientFile(): boolean {
    return this.patientFileCount > 0;
  }

  get weatherCount(): number {
    const h = this.result && this.result.history;
    if (!h) {
      return 0;
    }
    return (
      (h.temperatureCelsius !== null && h.temperatureCelsius !== undefined ? 1 : 0) +
      (h.humidityPercent !== null && h.humidityPercent !== undefined ? 1 : 0)
    );
  }

  get impressionCount(): number {
    const a = this.result && this.result.assessment;
    if (!a) return 0;
    let n =
      (a.impressions ? 1 : 0) +
      (a.dryEyeType ? 1 : 0) +
      (a.dryEyeSeverity ? 1 : 0) +
      (a.treatmentPerformed ? 1 : 0) +
      (a.recommendedPlan ? 1 : 0) +
      (a.goalsForPatient ? 1 : 0);
    const rt = a.recommendedTreatments;
    if (rt) {
      n +=
        (rt.otc || []).length +
        (rt.rx || []).length +
        (rt.procedures || []).length +
        (rt.exercises || []).length;
    }
    return n;
  }

  get hasPatientProfile(): boolean {
    const p = this.result && this.result.patientProfile;
    if (!p) return false;
    return !!(
      p.firstName ||
      p.lastName ||
      p.email ||
      p.dateOfBirth ||
      p.address ||
      p.healthCardNumber ||
      p.phoneNumber ||
      p.age !== null ||
      p.gender
    );
  }

  get hasHistory(): boolean {
    const h = this.result && this.result.history;
    if (!h) return false;
    return !!(
      h.chiefComplaint ||
      (h.conditions && h.conditions.length) ||
      h.drugAllergies ||
      h.seasonalAllergies ||
      h.otherAllergies ||
      h.occupation ||
      h.hobbies ||
      h.triggeringEvent ||
      h.previousTreatments ||
      h.sleepPerNight !== null ||
      h.caffeinatedDrinksPerDay !== null ||
      h.makeupNotes ||
      h.dietQuality !== null ||
      h.healthQuality !== null ||
      h.stressLevel !== null ||
      h.workHoursPerDay !== null ||
      h.exerciseHoursPerWeek !== null ||
      (h.environmentalSymptoms && h.environmentalSymptoms.length > 0)
    );
  }

  get hasMedications(): boolean {
    const h = this.result && this.result.history;
    if (!h) return false;
    return !!(h.medications || h.currentOcularMedications || h.nutraceuticals);
  }

  formatQualityScale(value: number | null, lowLabel: string, highLabel: string): string {
    return formatScale10(value, { low: lowLabel, high: highLabel });
  }

  get hasWeather(): boolean {
    const h = this.result && this.result.history;
    if (!h) {
      return false;
    }
    return (
      (h.humidityPercent !== null && h.humidityPercent !== undefined) ||
      (h.temperatureCelsius !== null && h.temperatureCelsius !== undefined)
    );
  }

  get hasSymptoms(): boolean {
    const s = this.result && this.result.symptoms;
    if (!s) return false;
    return (
      (s.osdiScore !== null && s.osdiScore !== undefined) ||
      (s.speedScore !== null && s.speedScore !== undefined) ||
      (s.speedIIScore !== null && s.speedIIScore !== undefined) ||
      (s.osdi6Score !== null && s.osdi6Score !== undefined) ||
      (s.deqScore !== null && s.deqScore !== undefined) ||
      (s.fitzpatrickScore !== null && s.fitzpatrickScore !== undefined)
    );
  }

  get hasImpression(): boolean {
    const a = this.result && this.result.assessment;
    if (!a) return false;
    return !!(
      a.impressions ||
      a.dryEyeType ||
      a.dryEyeSeverity ||
      a.treatmentPerformed ||
      a.recommendedPlan ||
      a.goalsForPatient
    );
  }

  removedSummaryFields = new Set<string>();

  get hasLeftPaired(): boolean {
    return this.pairedRows.some(p => !!p.left);
  }

  get hasRightPaired(): boolean {
    return this.pairedRows.some(p => !!p.right);
  }

  /** Check if nothing meaningful was extracted */
  get nothingExtracted(): boolean {
    // Check if we have any findings
    const hasFindings = this.activeRowCount > 0;

    // Check if we have any meaningful data in other sections
    const hasData =
      this.hasPatientProfile ||
      this.hasHistory ||
      this.hasMedications ||
      this.hasSymptoms ||
      this.hasPatientFile ||
      this.hasImpression ||
      this.hasWeather;

    return !hasFindings && !hasData;
  }

  isSectionEnabled(key: string): boolean {
    const sections = this.effectiveEnabledSections;
    if (!sections || sections.length === 0) {
      return true;
    }
    return sections.indexOf(key) >= 0;
  }

  toggleSummaryField(key: string) {
    if (this.removedSummaryFields.has(key)) {
      this.removedSummaryFields.delete(key);
    } else {
      this.removedSummaryFields.add(key);
    }
  }

  isSummaryFieldRemoved(key: string): boolean {
    return this.removedSummaryFields.has(key);
  }

  @Output() apply = new EventEmitter<string[]>();
  @Output() reset = new EventEmitter<void>();
  @Output() rowToggled = new EventEmitter<FindingRow>();

  isApplying = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['result'] && !changes['result'].firstChange) {
      // Reset panel state when a new result is loaded (e.g. reapply from history)
      this.removedSummaryFields.clear();
      this.isApplying = false;
    }
  }

  onApply() {
    if (this.isApplying) return;
    this.isApplying = true;
    this.apply.emit(Array.from(this.removedSummaryFields));
    // Reset after a short delay so double-clicks are blocked but subsequent applies work
    setTimeout(() => {
      this.isApplying = false;
    }, 300);
  }

  trackByKey(_: number, row: FindingRow) {
    return row.original.systemKey;
  }

  trackByPair(_: number, pair: FindingPair) {
    return pair.signKey;
  }

  onRowToggled(row: FindingRow) {
    this.rowToggled.emit(row);
  }

  private readonly destroy$ = new Subject<void>();
  private removeLabel = 'Remove this field';
  private restoreLabel = 'Restore this field';

  constructor(private translocoService: TranslocoService) {}

  ngOnInit(): void {
    this.translocoService.selectTranslate('aiScribe.results.removeField')
      .pipe(takeUntil(this.destroy$))
      .subscribe(label => { this.removeLabel = label; });
    this.translocoService.selectTranslate('aiScribe.results.restoreField')
      .pipe(takeUntil(this.destroy$))
      .subscribe(label => { this.restoreLabel = label; });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fieldTooltip(key: string): string {
    return this.isSummaryFieldRemoved(key) ? this.restoreLabel : this.removeLabel;
  }

  private titleCasePipe = new TitleCasePipe();

  /** Title-cases a string value for display. Handles null/undefined/object gracefully. */
  tc(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value !== 'string') return String(value);
    return this.titleCasePipe.transform(value);
  }

  /**
   * Converts a camelCase condition/symptom key to a readable Title Case label.
   * e.g. "botoxInjections" → "Botox Injections", "runnyNose" → "Runny Nose"
   */
  formatConditionKey(key: string): string {
    if (!key) return '';
    // Insert a space before each uppercase letter, then title-case
    const spaced = key.replace(/([A-Z])/g, ' $1').trim();
    return this.titleCasePipe.transform(spaced);
  }

  private readonly ocularKeyLabels: Record<string, string> = {
    lasikOrPRK: 'LASIK or PRK',
    orthokeratology: 'Orthokeratology',
    myopia: 'Myopia',
    hyperopia: 'Hyperopia',
    presbyopia: 'Presbyopia',
    amblyopia: 'Amblyopia',
    strabismus: 'Strabismus',
    eyeTrauma: 'Eye Trauma',
    pterygium: 'Pterygium',
    glaucomaSurgery: 'Glaucoma Surgery',
    cataractSurgery: 'Cataract Surgery',
    strabismusSurgery: 'Strabismus Surgery',
    ptosisSurgery: 'Ptosis Surgery',
    retinalSurgery: 'Retinal Surgery',
    refractiveSurgery: 'Refractive Surgery',
  };

  formatOcularKey(key: string): string {
    if (!key) return '';
    return this.ocularKeyLabels[key] || this.formatConditionKey(key);
  }
}
